import React from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Loader2, ArrowLeft } from "lucide-react";

export default function VerifyStaffPage() {
  const { token } = useParams();
  const [state, setState] = React.useState({ loading: true, data: null, error: "" });

  React.useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    axios
      .get(`${base}/api/verify-staff/${encodeURIComponent(token)}`)
      .then((r) => setState({ loading: false, data: r.data, error: "" }))
      .catch((e) => setState({ loading: false, data: null, error: e.response?.data?.detail || "Could not verify" }));
  }, [token]);

  const { loading, data, error } = state;
  const ok = data && data.found && data.active;
  const notFound = data && !data.found;
  const expired = data && data.found && !data.active;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }); } catch { return iso; }
  };

  return (
    <div data-testid="verify-staff-page" className="min-h-screen bg-[#F5F5F5] py-12 px-4">
      <div className="max-w-md mx-auto">
        <Link to="/" data-testid="verify-back-link" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0A0A0A] mb-6">
          <ArrowLeft size={14} /> Back to raanah.com
        </Link>

        <div className="bg-white border border-[#0A0A0A]/10 overflow-hidden">
          {/* Brand bar */}
          <div className="bg-[#1E4D55] text-white px-6 py-5">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-80">Raanah · Staff ID verification</p>
            <p className="font-display text-2xl font-black tracking-tighter mt-1">Authenticity check</p>
          </div>

          {loading && (
            <div className="p-10 text-center" data-testid="verify-loading">
              <Loader2 size={28} className="animate-spin text-[#1E4D55] mx-auto" />
              <p className="text-sm text-[#475569] mt-3">Looking up verify code <span className="font-mono">{token}</span>&hellip;</p>
            </div>
          )}

          {!loading && (notFound || error) && (
            <div className="p-8" data-testid="verify-not-found">
              <div className="flex items-start gap-3 bg-[#FEE2E2] border border-[#FCA5A5] p-4">
                <ShieldAlert size={20} className="text-[#B91C1C] shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-bold text-[#B91C1C]">This ID could not be verified.</p>
                  <p className="text-sm text-[#7F1D1D] mt-1">
                    The verify code <span className="font-mono">{token}</span> is not in our system. If you found a Raanah staff ID, please contact us — see below.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!loading && ok && (
            <div className="p-6" data-testid="verify-success">
              <div className="flex items-start gap-3 bg-[#5BB247]/10 border border-[#5BB247]/40 p-4 mb-5">
                <ShieldCheck size={20} className="text-[#2c8642] shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-bold text-[#2c8642]">Verified Raanah staff member.</p>
                  <p className="text-sm text-[#1f5d2e] mt-1">Issued by Raanah Technologies and currently active.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                {data.photo_url && (
                  <img src={data.photo_url} alt={data.name} className="w-24 h-32 object-cover border border-[#0A0A0A]/10" />
                )}
                <div className="flex-1 space-y-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">Name</p>
                    <p className="font-display text-xl font-black text-[#0A0A0A] leading-tight">{data.name}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">Role</p>
                    <p className="font-semibold text-[#0A0A0A]">{data.role}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">Staff ID</p>
                      <p className="font-mono font-bold text-[#1E4D55] text-sm">#{data.employee_no}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">Valid until</p>
                      <p className="font-mono text-[#0A0A0A] text-sm">{fmtDate(data.valid_until)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && expired && (
            <div className="p-8" data-testid="verify-expired">
              <div className="flex items-start gap-3 bg-[#F5A623]/10 border border-[#F5A623]/40 p-4">
                <ShieldAlert size={20} className="text-[#A66800] shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-bold text-[#A66800]">This ID has expired.</p>
                  <p className="text-sm text-[#7A4F0F] mt-1">
                    {data.name} held a Raanah staff ID (#{data.employee_no}) until <strong>{fmtDate(data.valid_until)}</strong>. They are no longer authorised to act on behalf of Raanah Technologies.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Contact / lost-and-found */}
          {!loading && data && (
            <div className="bg-[#0A0A0A] text-white px-6 py-5">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/70">Lost & found · contact</p>
              <p className="mt-2 text-sm">If you have found a Raanah staff ID card, please call or post it back to us.</p>
              <p className="mt-2 font-mono"><strong>{data.hotline}</strong> · <a className="underline" href={`mailto:${data.contact_email}`}>{data.contact_email}</a></p>
              {data.return_address && <p className="mt-1 text-xs text-white/70">{data.return_address}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
